// assets/AllCourses/Exercises/VocabularyBuildingExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function VocabularyBuildingExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.vocabularyQuestions)
    ? exercise.vocabularyQuestions.map((q) => ({
        word: String(q.word || "").trim(),
        options: Array.isArray(q.options)
          ? q.options.map((o) => String(o).trim())
          : [],
        correctAnswer: String(q.correctAnswer || "").trim(),
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= STATE ================= */
  const [state, setState] = useState({
    answers: {},
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setState({
      answers: {},
      hasInteracted: false,
    });

    completedRef.current = false;
  }, [exercise._id]);

  useEffect(() => {
    if (!showResults) completedRef.current = false;
  }, [showResults]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("vocabularyQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[qIdx] = { ...next[qIdx], [field]: value };
    updateQuestions(next);
  };

  const handleOptionChange = (qIdx, optIdx, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const q = next[qIdx];
    const options = [...q.options];
    options[optIdx] = value;

    next[qIdx] = {
      ...q,
      options,
      correctAnswer: options.includes(q.correctAnswer)
        ? q.correctAnswer
        : "",
    };

    updateQuestions(next);
  };

  const handleAddOption = (qIdx) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[qIdx].options.push("");
    updateQuestions(next);
  };

  const handleRemoveOption = (qIdx, optIdx) => {
    if (!isEditMode) return;

    const next = [...questions];
    const q = next[qIdx];
    const options = q.options.filter((_, i) => i !== optIdx);

    next[qIdx] = {
      ...q,
      options,
      correctAnswer: options.includes(q.correctAnswer)
        ? q.correctAnswer
        : "",
    };

    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;

    updateQuestions([
      ...questions,
      {
        word: "",
        options: [],
        correctAnswer: "",
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;

    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT INPUT ================= */
  const handleChange = (qIdx, value) => {
    if (isEditMode || showResults) return;

    setState((prev) => {
      const answers = {
        ...prev.answers,
        [qIdx]: value,
      };

      const next = {
        answers,
        hasInteracted: true,
      };

      setAttempt(next);

      // 🔥 IMPORTANT: ALWAYS UPDATE PARENT
      onComplete(answers, false);

      const allAnswered = questions.every(
        (_, i) => answers[i] !== undefined
      );

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every((q, i) => {
          return (
            normalize(answers[i], q.requireCapitalization) ===
            normalize(q.correctAnswer, q.requireCapitalization)
          );
        });

        onComplete(answers, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Word ${qIdx + 1}`}
              value={q.word}
              onChange={(v) =>
                handleQuestionChange(qIdx, "word", v)
              }
              placeholder="abundant"
              required
            />

            <div className="space-y-3">
              {q.options.map((opt, optIdx) => (
                <div key={optIdx} className="flex gap-3">
                  <StringField
                    value={opt}
                    onChange={(v) =>
                      handleOptionChange(qIdx, optIdx, v)
                    }
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() =>
                      handleRemoveOption(qIdx, optIdx)
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}

              <Button
                size="sm"
                onClick={() => handleAddOption(qIdx)}
              >
                Add Option
              </Button>
            </div>

            <StringField
              label="Correct Answer"
              value={q.correctAnswer}
              onChange={(v) =>
                handleQuestionChange(qIdx, "correctAnswer", v)
              }
            />

            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT VIEW ================= */
  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Vocabulary Builder</CardTitle>
        <CardDescription>
          Choose correct meaning
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const selected = state.answers[qIdx];

          return (
            <div key={qIdx} className="space-y-6">
              <div className="text-3xl font-bold text-center">
                {q.word}
              </div>

              <RadioGroup
                value={selected ?? ""}
                onValueChange={(v) =>
                  handleChange(qIdx, v)
                }
                disabled={showResults}
              >
                {q.options.map((opt, optIdx) => {
                  const correct =
                    normalize(opt, q.requireCapitalization) ===
                    normalize(q.correctAnswer, q.requireCapitalization);

                  const isSelected = selected === opt;

                  return (
                    <div
                      key={optIdx}
                      className={cn(
                        "p-4 border rounded-lg",

                        // selected before submit
                        isSelected && !showResults && "bg-accent",

                        // correct answer ALWAYS green
                        showResults && correct && "bg-green-100 border-green-400",

                        // wrong selected
                        showResults &&
                          isSelected &&
                          !correct &&
                          "bg-red-100 border-red-400"
                      )}
                    >
                      <RadioGroupItem value={opt} />
                      <Label className="ml-3">
                        {opt}
                        {showResults && isSelected && (
                          <span className="ml-2 text-sm">
                            {correct
                              ? "Correct"
                              : `Correct: ${q.correctAnswer}`}
                          </span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}